import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { isPlatformBrowser, NgOptimizedImage } from '@angular/common';
import { Router } from '@angular/router';
import type { Params } from '@angular/router';
import type {
  HeroAngledMediaSlide,
  HeroBackgroundSlide,
  HeroCarouselData,
  HeroImageAsset,
  HeroMediaRightSlide,
  HeroSlide,
  HeroTilesSlide,
} from '../../models/hero.model';

const DEFAULT_AUTOPLAY_MS = 7000;
const EMPTY_HERO_DATA: HeroCarouselData = {
  loading: true,
  autoplayMs: DEFAULT_AUTOPLAY_MS,
  slides: [],
};

@Component({
  selector: 'app-hero',
  imports: [NgOptimizedImage],
  templateUrl: './hero.component.html',
  styleUrl: './hero.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroComponent {
  heroData = input<HeroCarouselData>(EMPTY_HERO_DATA);

  @ViewChild('root') private rootRef?: ElementRef<HTMLElement>;

  protected readonly currentIndex = signal(0);
  protected readonly isPaused = signal(false);
  protected readonly loading = computed(() => this.heroData().loading ?? false);
  protected readonly slides = computed(() => this.heroData().slides);
  protected readonly autoplayMs = computed(
    () => this.heroData().autoplayMs ?? DEFAULT_AUTOPLAY_MS,
  );
  protected readonly slideCount = computed(() => this.slides().length);
  protected readonly shouldRender = computed(() => this.loading() || this.slideCount() > 0);
  protected readonly currentSlide = computed<HeroSlide | null>(() => {
    const slides = this.slides();
    if (!slides.length) {
      return null;
    }

    return slides[this.currentIndex()] ?? slides[0];
  });
  protected readonly isReducedMotion = signal(false);
  protected readonly indicatorsAnimated = computed(
    () =>
      this.isBrowser &&
      !this.loading() &&
      !this.isReducedMotion() &&
      this.slideCount() > 1,
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  constructor() {
    if (this.isBrowser) {
      this.bindReducedMotionPreference();
    }

    effect(
      (onCleanup) => {
        const slides = this.slides();
        const currentIndex = this.currentIndex();
        const isLoading = this.loading();
        const isPaused = this.isPaused();
        const autoplayMs = this.autoplayMs();
        const isReducedMotion = this.isReducedMotion();

        if (!slides.length) {
          this.currentIndex.set(0);
          return;
        }

        if (currentIndex >= slides.length) {
          this.currentIndex.set(0);
          return;
        }

        if (!this.isBrowser || isLoading || isPaused || isReducedMotion || slides.length < 2) {
          return;
        }

        const timerId = window.setInterval(() => {
          this.next();
        }, autoplayMs);

        onCleanup(() => {
          window.clearInterval(timerId);
        });
      },
    );
  }

  protected goTo(index: number): void {
    if (index < 0 || index >= this.slideCount()) {
      return;
    }

    this.currentIndex.set(index);
  }

  protected next(): void {
    const total = this.slideCount();
    if (total < 2) {
      return;
    }

    this.currentIndex.update((index) => (index + 1) % total);
  }

  protected pause(): void {
    if (this.slideCount() < 2) {
      return;
    }

    this.isPaused.set(true);
  }

  protected resume(): void {
    if (this.isReducedMotion()) {
      return;
    }

    this.isPaused.set(false);
  }

  protected onFocusOut(event: FocusEvent): void {
    const nextTarget = event.relatedTarget;
    const root = this.rootRef?.nativeElement;

    if (root && nextTarget instanceof Node && root.contains(nextTarget)) {
      return;
    }

    this.resume();
  }

  protected formatCounter(value: number): string {
    return value.toString().padStart(2, '0');
  }

  protected isSlideActive(index: number): boolean {
    return index === this.currentIndex();
  }

  protected getTilesSlide(slide: HeroSlide): HeroTilesSlide {
    return slide as HeroTilesSlide;
  }

  protected getMediaRightSlide(slide: HeroSlide): HeroMediaRightSlide {
    return slide as HeroMediaRightSlide;
  }

  protected getAngledMediaSlide(slide: HeroSlide): HeroAngledMediaSlide {
    return slide as HeroAngledMediaSlide;
  }

  protected getBackgroundSlide(slide: HeroSlide): HeroBackgroundSlide {
    return slide as HeroBackgroundSlide;
  }

  protected getImageFocalPoint(
    image: HeroImageAsset,
    viewport: 'desktop' | 'mobile',
  ): string {
    const focalPoint =
      viewport === 'mobile'
        ? image.mobileFocalPoint ?? image.desktopFocalPoint
        : image.desktopFocalPoint ?? image.mobileFocalPoint;

    const x = this.clampPercentage(focalPoint?.x ?? 50);
    const y = this.clampPercentage(focalPoint?.y ?? 50);

    return `${x}% ${y}%`;
  }

  protected shouldPrioritizeSlide(index: number): boolean {
    return index === 0;
  }

  protected shouldPrioritizeTile(slideIndex: number, tileIndex: number): boolean {
    return slideIndex === 0 && tileIndex === 0;
  }

  protected trackSlide(index: number, slide: HeroSlide): string {
    return `${slide.id}-${index}`;
  }

  protected isExternalRoute(route: string | null | undefined): boolean {
    return /^https?:\/\//i.test(route ?? '');
  }

  protected actionHref(route: string, queryParams?: Params): string {
    return this.appendQueryParams(route || '/', queryParams);
  }

  protected onActionClick(event: MouseEvent, route: string, queryParams?: Params): void {
    if (this.isExternalRoute(route)) return;

    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    this.router.navigateByUrl(this.actionHref(route, queryParams));
  }

  private clampPercentage(value: number): number {
    return Math.min(100, Math.max(0, value));
  }

  private appendQueryParams(route: string, queryParams?: Params): string {
    const query = this.serializeQueryParams(queryParams);
    if (!query) return route;
    return `${route}${route.includes('?') ? '&' : '?'}${query}`;
  }

  private serializeQueryParams(queryParams?: Params): string {
    if (!queryParams) return '';

    const params = new URLSearchParams();
    for (const [key, rawValue] of Object.entries(queryParams)) {
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      for (const value of values) {
        if (value === null || value === undefined) continue;
        params.append(key, String(value));
      }
    }

    return params.toString();
  }

  private bindReducedMotionPreference(): void {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const syncPreference = (matches: boolean): void => {
      this.isReducedMotion.set(matches);

      if (matches) {
        this.isPaused.set(true);
      }
    };

    syncPreference(query.matches);

    const onPreferenceChange = (event: MediaQueryListEvent): void => {
      syncPreference(event.matches);
    };

    query.addEventListener('change', onPreferenceChange);

    this.destroyRef.onDestroy(() => {
      query.removeEventListener('change', onPreferenceChange);
    });
  }
}
